import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ReactComponent as DragImage } from "../../assets/images/icons/DragIcon.svg";
import { useFetchFunnelStagesQuery, useLinkStageWithTaskMutation } from "../../utils/customer/funnelSlice";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../utils/loaderSlice";
import { pushAlert } from "../../utils/alertSlice";
import moment from "moment";
import { Diversity1 } from "@mui/icons-material";
import { identity } from "@fullcalendar/core/internal";
import { hexToRgba } from "../../utils/utilityFunctions/color";
import { ReactComponent as CircleIcon } from "../../assets/images/icons/CircleIcon.svg";
import { truncateString } from "../../utils/utilityFunctions/stringUtility";

const FunnelSubDrawer = ({ activeFunnel, funnelStages, setFunnelStages, taskid, Funnel }) => {
  const dispatch = useDispatch();

  const [stageLinked, setStageLinked] = useState({});

  const {
    data: AllFunnelStages,
    refetch: RefetchStage,
    isLoading: FunnelStagesLoading,
    isError: isFunnelStagesError,
    error: FunnelStagesErrorData,
    isSuccess: FunnelStagesSuccess,
  } = useFetchFunnelStagesQuery({
    funnel_id: activeFunnel,
    task_id: taskid?.id,
  });

  const [
    LinkStageTask,
    {
      isLoading: LinkStageTaskLoading,
      isError: isLinkStageTaskError,
      error: LinkStageTaskErrorData,
      isSuccess: LinkStageTaskSuccess,
      data: LinkStageTaskData,
    },
  ] = useLinkStageWithTaskMutation();

  useEffect(() => {
    //loading
    if (FunnelStagesLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }

    //try block
    if (FunnelStagesSuccess && AllFunnelStages?.data) {
      setFunnelStages(AllFunnelStages?.data);
    }

    // catch block
    if (isFunnelStagesError) {
      dispatch(pushAlert({ message: FunnelStagesErrorData?.data?.message, type: "danger", visible: true }));
    }
  }, [FunnelStagesSuccess, isFunnelStagesError, FunnelStagesLoading, AllFunnelStages?.data]);

  useEffect(() => {
    if (funnelStages?.length > 0) {
      const initialStageState = {};
      funnelStages?.forEach((stage) => {
        initialStageState[stage?.stage_id] = Boolean(stage?.is_checked);
      });
      setStageLinked(initialStageState);
    }
  }, [funnelStages]);

  const handleStageComplete = async (stage) => {
    if (!stage) return;
    console.log("Toggling stage:", stage);

    const linkPayload = {
      type_id: Funnel?.id ?? null,
      task_id: taskid?.id,
      stage_id: stage?.stage_id,
    };

    try {
      if (!stage?.is_checked) {
        await LinkStageTask(linkPayload).unwrap();
      }
    } catch (err) {
      // Rollback if API fails
      setStageLinked((prev) => ({
        ...prev,
        [stage.stage_id]: !prev[stage.stage_id],
      }));

      dispatch(
        pushAlert({
          message: err?.data?.message || "Failed to update stage",
          type: "danger",
          visible: true,
        })
      );
    }
  };

  useEffect(() => {
    // ---- Loader handling ----
    if (LinkStageTaskLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }

    // ---- Success handling ----
    if (LinkStageTaskSuccess && LinkStageTaskData?.status) {
      RefetchStage();
      dispatch(
        pushAlert({
          message: LinkStageTaskData?.data?.message || "Stage updated successfully",
          type: "success",
          visible: true,
        })
      );
    }

    // ---- Error handling ----
    if (isLinkStageTaskError) {
      dispatch(
        pushAlert({
          message: LinkStageTaskErrorData?.data?.message || "Failed to link stage",
          type: "danger",
          visible: true,
        })
      );
    }
  }, [LinkStageTaskLoading, LinkStageTaskSuccess, isLinkStageTaskError, LinkStageTaskErrorData, LinkStageTaskData]);

  const highlightTextInHtml = (html, highlight) => {
    if (!highlight.trim()) return html;

    const regex = new RegExp(`(${highlight})`, "gi");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null, false);
    let node;
    while ((node = walker.nextNode())) {
      const text = node.textContent;
      if (regex.test(text)) {
        const span = document.createElement("span");
        span.innerHTML = text.replace(regex, (match) => `<span class="bg-yellow-300">${match}</span>`);
        node.replaceWith(span);
      }
    }

    return tempDiv.innerHTML;
  };

  const ParagraphComponent = ({ CategoryData, searchTerm }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showReadMore, setShowReadMore] = useState(false);
    const paragraphRef = useRef(null);

    useLayoutEffect(() => {
      const checkHeight = () => {
        if (paragraphRef.current) {
          const { scrollHeight, clientHeight } = paragraphRef.current;

          setShowReadMore(scrollHeight > clientHeight);
        }
      };

      checkHeight();
    }, [CategoryData]);

    const handleReadMoreClick = (e) => {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    };

    return (
      <div className="space-y-1 w-full">
        <p
          ref={paragraphRef}
          className={`text-[11px] text-wrap font-normal text-justify text-[#666] w-full break-words ${
            isExpanded ? " " : "line-clamp-1"
          }`}
          dangerouslySetInnerHTML={{
            __html: highlightTextInHtml(CategoryData, searchTerm),
          }}
        ></p>

        {showReadMore && (
          <button
            className="text-blue-500 hover:text-blue-600 font-300 text-xs cursor-pointer"
            onClick={(e) => handleReadMoreClick(e)}
          >
            {isExpanded ? "Read less" : "Read more"}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="w-full h-full flex flex-col items-center justify-start bg-white p-1 overflow-y-auto thinscrollbar">
        {funnelStages?.length > 0 ? (
          funnelStages?.map((stage, index) => {
            {
              console.log("stage", stage);
            }
            return (
              <>
                <div
                  key={index}
                  className="w-full flex items-start justify-start gap-x-2 gap-y-2 px-3 py-2 border-dashed border-b hover:bg-[#f2f2f2bb] cursor-pointer"
                >
                  {taskid?.id && (
                    <div className="">
                      <input
                        type="checkbox"
                        className=" h-4 w-4 rounded border-gray-400 bg-white text-[#2999BC] focus:ring-0 focus:outline-none cursor-pointer"
                        checked={stageLinked[stage?.stage_id]}
                        onChange={() => handleStageComplete(stage)}
                      />
                    </div>
                  )}

                  <div className="w-full relative flex flex-col  gap-2 rounded-md">
                    <div className="flex items-center gap-2 w-full">
                      <div
                        className="flex items-center gap-2 text-[#383838] font-poppins text-[11px] rounded-md px-2 py-[2px]"
                        style={{
                          backgroundColor:
                            stage.color && stage.color !== "#FFFFFF" ? hexToRgba(stage.color, 0.08) : "#E4E6E9",
                        }}
                      >
                        <CircleIcon fill={stage.color && stage.color !== "#FFFFFF" ? stage.color : "#383838"} />
                        <span
                          style={{
                            color: stage.color && stage.color !== "#FFFFFF" ? stage.color : "#383838",
                          }}
                        >
                          Stage {index + 1}
                        </span>
                      </div>

                      <h6 className="text-sm font-semibold text-gray-700  flex-1 pb-1 truncate">
                        {truncateString({ str: stage?.stage_name }, 5)}
                      </h6>
                    </div>

                    <ParagraphComponent CategoryData={stage?.description} searchTerm={""} />

                    {/* <p className="text-sm text-[#666666] px-">{stage?.description || "No description available"}</p> */}

                    {stage?.updated_by && stage?.updated_at && (
                      <div className="flex justify-start items-center gap-x-2 ">
                        <span className="text-[#13131399] text-xs font-normal">{stage?.updated_by}</span>
                        <span className="text-[#13131399] font-normal">|</span>

                        <span className="text-[#13131399] text-xs font-normal">
                          {moment(stage?.updated_at).format("DD MMM YYYY, hh:mm A")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-sm text-gray-500">No funnel stages available</p>
          </div>
        )}
      </div>
    </>
  );
};

export default FunnelSubDrawer;
